import React, { useEffect, useState } from 'react';
import { getPhotos } from '../services/apiService';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ZoomIn, 
  Image as ImageIcon, 
} from 'lucide-react';
const Gallery = () => {

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    setLoading(true);
    getPhotos(selectedCategory)
      .then((data) => {
        // Filter out photos with blob URLs
        const validPhotos = data.filter(photo => {
          const url = photo.url || photo.photo_url || photo.imageUrl;
          return url && !url.startsWith('blob:');
        });
        setPhotos(validPhotos);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [selectedCategory]);

  const categories = [
    { id: 'all', name: 'All Moments' },
    { id: 'visa', name: 'Visa Success' },
    { id: 'events', name: 'Campus Events' },
    { id: 'training', name: 'Classroom' },
    { id: 'students', name: 'Student Life' },
  ];

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      
      {/* =========================================
          HERO HEADER
      ========================================= */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl md:text-5xl mt-5 font-serif font-bold text-slate-900 mb-3 md:mb-4">
              Life at <span className="text-brandRed">6th Sense.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto">
              Behind the scenes of our classrooms, events, and success celebrations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* =========================================
          FILTER TABS (Horizontal Scroll)
      ========================================= */}
      <div className="sticky top-[60px] md:top-[70px] z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 py-3 md:py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-nowrap overflow-x-auto no-scrollbar gap-2 md:justify-center pb-1">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 md:px-5 rounded-full text-sm font-bold whitespace-nowrap transition-all border flex-shrink-0 ${
                  selectedCategory === cat.id
                    ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-red-500 hover:text-red-500'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* =========================================
          GALLERY GRID
      ========================================= */}
      <section className="py-8 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="aspect-square bg-slate-200 rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : (
            <>
              {photos.length > 0 ? (
                <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  <AnimatePresence>
                    {photos.map((photo, index) => (
                      <motion.div
                        layout
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.3 }}
                        key={photo.id || index}
                        className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl bg-slate-100 border border-slate-200 shadow-sm hover:shadow-xl transition-all"
                        onClick={() => setSelectedPhoto(photo)}
                      >
                        <img
                          src={(() => {
                            const url = photo.url || photo.photo_url || photo.imageUrl;
                            return url && !url.startsWith('blob:') ? url : null;
                          })()}
                          alt={photo.title || 'Gallery Image'}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        
                        {/* Hover Overlay - Hidden on touch devices usually, acts as tap feedback */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <div className="text-white text-center p-2 md:p-4">
                            <ZoomIn size={24} className="mx-auto mb-2 md:w-8 md:h-8" />
                            {photo.title && (
                              <p className="text-xs md:text-sm font-medium line-clamp-2 hidden md:block">{photo.title}</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
                  <ImageIcon size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No photos found in this category.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* =========================================
          LIGHTBOX MODAL
      ========================================= */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-black/95 backdrop-blur-md"
            onClick={() => setSelectedPhoto(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full h-full md:h-auto md:max-w-5xl md:max-h-[90vh] flex flex-col items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 right-4 z-50 text-white/80 hover:text-white bg-black/20 hover:bg-white/20 p-2 rounded-full transition-colors backdrop-blur-sm"
              >
                <X size={24} />
              </button>

              <img
                src={selectedPhoto.url || selectedPhoto.photo_url || selectedPhoto.imageUrl}
                alt={selectedPhoto.title}
                className="max-h-[85vh] w-auto max-w-full md:rounded-lg shadow-2xl object-contain"
              />

              {(selectedPhoto.title || selectedPhoto.caption) && (
                <div className="absolute bottom-0 left-0 right-0 p-4 md:static md:mt-4 text-center bg-black/50 md:bg-transparent backdrop-blur-md md:backdrop-blur-none text-white">
                  <p className="text-lg font-medium">{selectedPhoto.title}</p>
                  {selectedPhoto.caption && (
                    <p className="text-white/70 text-sm mt-1">{selectedPhoto.caption}</p>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Gallery;